import { useQuery } from "@apollo/client/react";
import { useAuth, useUser } from "@clerk/clerk-react";
import { useNavigate, useParams } from "react-router-dom";

import { Logo } from "@/components/atoms/Logo";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import { Navigation, Queries, Values } from "@/constants";
import { Card } from "@/components/ui/card";

/** What an invitation asks: to take up someone's books, or to join a business. */
export type InvitationKind = "books" | "business";

/** An invitation as the page says it, whichever kind it is. */
type Invitation = {
  /** What it is, in a sentence. */
  said: string;
  /** The address it was sent to - none asked of a verified firm itself. */
  emailAddress: string;
  /** The firm it was asked of itself, whose owners take it up. */
  firmName: string | null;
  /** What it is answered by, once signed in as the one asked. */
  done: string;
};

/** What the page stands for while the invitation is read: a typical one. */
const PLACEHOLDER: Invitation = {
  said: "Jane Cooper (jane@example.com) has shared Cooper Studio's books with you on NeoLedger, as their accountant.",
  emailAddress: "accountant@example.com",
  firmName: null,
  done: "take them up for your accounting or bookkeeping firm",
};

/**
 * An invitation, as the link in the email sent with it opens - before signing
 * in: what it is (books someone shares, or a business to join), then on to
 * sign in, or make an account, with the address it was sent to. Signed in as
 * someone else, it says so and switches them over; signed in as the one asked,
 * it goes on to where the invitation is answered.
 */
export default function Page({ kind }: { kind: InvitationKind }) {
  const { id: identifier } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isLoaded, isSignedIn, signOut } = useAuth();
  const { user } = useUser();
  const books = useQuery(Queries.PUBLIC_EXTERNAL_GRANT_QUERY, {
    variables: { id: btoa(`PublicExternalGrantObject:${identifier}`) },
    skip: kind !== "books",
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });
  const business = useQuery(Queries.PUBLIC_ACCOUNT_QUERY, {
    variables: { id: btoa(`PublicAccountObject:${identifier}`) },
    skip: kind !== "business",
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });
  const loading = kind === "books" ? books.loading : business.loading;
  const grant =
    books.data?.node?.__typename === "PublicExternalGrantObject"
      ? books.data.node
      : null;
  const membership =
    business.data?.node?.__typename === "PublicAccountObject"
      ? business.data.node
      : null;

  let invitation: Invitation | null = null;
  if (grant) {
    const who = grant.sharedByName
      ? `${grant.sharedByName} (${grant.sharedByEmailAddress})`
      : grant.sharedByEmailAddress;
    const books = grant.isUserEntity
      ? "their books"
      : `${grant.entityName}'s books`;
    invitation = {
      // Asked of a firm itself, or of the person reading.
      said: `${who} has shared ${books} with ${grant.granteeName ? grant.granteeName : "you"} on ${Values.COMPANY_NAME}, as their accountant.`,
      emailAddress: grant.emailAddress,
      firmName: grant.granteeName ? grant.granteeName : null,
      done: "take them up for your accounting or bookkeeping firm",
    };
  } else if (membership) {
    invitation = {
      said: `You've been invited to join ${membership.entityName} on ${Values.COMPANY_NAME}.`,
      emailAddress: membership.emailAddress,
      firmName: null,
      done: `join ${membership.entityName}`,
    };
  }
  const shown = invitation ?? PLACEHOLDER;
  // Where it is answered, once signed in.
  const answeredAt =
    kind === "books"
      ? `/app/invitations?external_grant=${identifier}`
      : `/app/invitations?account=${identifier}`;
  const signInTo = (route: string) => {
    const params = new URLSearchParams({ redirect_url: answeredAt });
    if (shown.emailAddress) params.set("email_address", shown.emailAddress);
    return `${route}?${params}`;
  };

  // Signed in as whom - and whether as the one it was sent to.
  const signedInAs = user?.primaryEmailAddress?.emailAddress ?? null;
  const isSignedInAsAsked =
    shown.emailAddress === "" ||
    (user?.emailAddresses ?? []).some(
      (address) =>
        address.emailAddress.toLowerCase() === shown.emailAddress.toLowerCase(),
    );
  const isReadingWho = loading || !isLoaded;

  let then: string;
  if (!isSignedIn) {
    then = shown.firmName
      ? `Its owners sign in to take them up for ${shown.firmName}.`
      : `Sign in as ${shown.emailAddress}, or create your account with it, to ${shown.done}.`;
  } else if (!isSignedInAsAsked) {
    then = `You're signed in as ${signedInAs}, and it was sent to ${shown.emailAddress}: sign out, then sign in as ${shown.emailAddress} - or create your account with it - to ${shown.done}.`;
  } else if (shown.firmName) {
    then = `You're signed in as ${signedInAs}: its owners take them up for ${shown.firmName}.`;
  } else {
    then = `You're signed in as ${signedInAs}.`;
  }

  return (
    <div className="min-h-screen bg-muted flex flex-col items-center justify-center pl-4 pr-4 pt-12 pb-12 gap-6">
      <Logo variant="logo-with-text" />
      <Card
        data-testid="invitation"
        className="w-full max-w-120 p-6 flex flex-col gap-4"
      >
        {!loading && invitation === null ? (
          <>
            <Typography level="h3">
              This invitation is no longer open
            </Typography>
            <Typography level="body-md" className="text-muted-foreground">
              {kind === "books"
                ? "It was taken up, turned down or taken back - or the link is not one we sent. Ask whoever shared their books for a new one."
                : "It was accepted, turned down or taken back - or the link is not one we sent. Ask whoever invited you for a new one."}
            </Typography>
            <div>
              <Button
                variant="outline"
                onClick={() =>
                  navigate(
                    isSignedIn
                      ? Navigation.ROUTES.APP.PAGE
                      : Navigation.ROUTES.AUTH.LOGIN,
                  )
                }
              >
                {isSignedIn ? "Open the app" : "Sign in"}
              </Button>
            </div>
          </>
        ) : (
          <>
            <Typography level="h3">
              <Skeleton variant="inline" loading={loading}>
                You've been invited
              </Skeleton>
            </Typography>
            <Typography level="body-md">
              <Skeleton variant="inline" loading={loading}>
                {shown.said}
              </Skeleton>
            </Typography>
            <Typography level="body-sm" className="text-muted-foreground">
              <Skeleton variant="inline" loading={isReadingWho}>
                {then}
              </Skeleton>
            </Typography>
            <div className="flex flex-wrap gap-2 pt-2">
              {isSignedIn && !isSignedInAsAsked ? (
                <Skeleton variant="rectangular" loading={isReadingWho}>
                  <Button
                    data-testid="invitation-switch"
                    onClick={() =>
                      void signOut({
                        redirectUrl: signInTo(Navigation.ROUTES.AUTH.LOGIN),
                      })
                    }
                  >
                    Sign out and switch
                  </Button>
                </Skeleton>
              ) : isSignedIn ? (
                <Skeleton variant="rectangular" loading={isReadingWho}>
                  <Button
                    data-testid="invitation-continue"
                    // Asked of a firm itself, it is its owners': the app asks
                    // them first thing, and nobody else.
                    onClick={() =>
                      navigate(
                        shown.firmName
                          ? Navigation.ROUTES.APP.PAGE
                          : answeredAt,
                      )
                    }
                  >
                    {shown.firmName ? "Open the app" : "Continue"}
                  </Button>
                </Skeleton>
              ) : (
                <>
                  {shown.firmName ? null : (
                    <Skeleton variant="rectangular" loading={isReadingWho}>
                      <Button
                        data-testid="invitation-sign-up"
                        onClick={() =>
                          navigate(signInTo(Navigation.ROUTES.AUTH.SIGNUP))
                        }
                      >
                        Create your account
                      </Button>
                    </Skeleton>
                  )}
                  <Skeleton variant="rectangular" loading={isReadingWho}>
                    <Button
                      variant={shown.firmName ? "default" : "outline"}
                      data-testid="invitation-sign-in"
                      onClick={() =>
                        navigate(signInTo(Navigation.ROUTES.AUTH.LOGIN))
                      }
                    >
                      Sign in
                    </Button>
                  </Skeleton>
                </>
              )}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
